import React, { useMemo, useState } from 'react';
import TrackerIcon from './TrackerIcon';
import { TrackerCatalyst } from '../../types';
import { fmtLong } from './helpers';
import { readThrough } from './readThrough';

interface Props {
  allRows: TrackerCatalyst[];
  row: TrackerCatalyst;
  onOpen: (id: string) => void;
}

const RT_HELP = "Each row: whether this result raises or lowers the chance that a pending event is positive (primary endpoint met, data supportive, or approval). The pending event's P(+) is adjusted automatically. Class signals count only between drugs of the same modality (e.g. ADC to ADC, TKI to TKI). Class signal (up to ±15): a same-mechanism or same-target result makes the biology more or less likely to work; weaker for other tumors and crowded classes, and for early-phase data. Efficacy bar (−3 to −8): an approval or practice-changing Phase 3 in the same tumor, line and population raises the bar for events that read out after it, most for trials started after it or early/single-arm data; only the highest bar counts. Several class signals in the same direction add with diminishing returns. A competitor's failure does not change your odds. Total capped at ±25.";

/** rtCardHTML()/rtListHTML() (tracker.js:833-840): the detail-dialog read-through browsing panel —
 * which pending events this past readout changes the odds for, and why. Shown only while the card is
 * collapsed (tracker.js:1025's `open ? "" : rtCardHTML(r)`), same as the original. */
const ReadThroughPanel: React.FC<Props> = ({ allRows, row, onOpen }) => {
  const [open, setOpen] = useState(false);
  const items = useMemo(() => readThrough(allRows, row), [allRows, row]);
  if (!items.length) return null;
  const up = items.filter((i) => i.eff > 0).length;
  const dn = items.filter((i) => i.eff < 0).length;

  return (
    <div className="rtc">
      <div className="rtrow">
        <button type="button" className="rtx" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          <TrackerIcon name="zap" />
          <span>Read-through: changes the odds for {items.length} pending event{items.length === 1 ? '' : 's'}</span>
          {up > 0 && <span className="u">&#9650;{up}</span>}
          {dn > 0 && <span className="d">&#9660;{dn}</span>}
          <span className="cv">{open ? '−' : '+'}</span>
        </button>
        <span className="rthelp tip-l" tabIndex={0} title={RT_HELP} aria-label="How read-through works">?</span>
      </div>
      {open && (
        <div className="rtscroll">
          <div className="rtl">
            {items.map((it) => (
              <button type="button" key={it.x.id} className={`rti ${it.eff > 0 ? 'up' : it.eff < 0 ? 'dn' : ''}`} onClick={() => onOpen(it.x.id)}>
                <span className="rtd">{it.eff > 0 ? '▲ Raises odds' : '▼ Lowers odds'}</span>
                <b>{it.x.asset}</b>
                <span className="s">{it.x.company} · {it.x.trial || it.x.phase || ''} · {it.x.type || ''} · {it.x.window || fmtLong(it.x.date)}</span>
                <span className="s rtw">{it.why}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReadThroughPanel;
